import React, { useState, FC, ReactNode } from "react";
import { Alert, AlertTitle, Typography, IconButton, Collapse, Box } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Chunk } from "@/contexts/DealContext";

interface ExpandableAlertProps {
  title: string;
  chunk: Chunk;
  maxHeight?: number; // optional custom height
}

function normalizeChar(char: string): string {
  return char
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function normalizeWithMapping(str: string) {
  const normalizedChars: string[] = [];
  const indexMap: number[] = [];

  let lastWasSpace = false;

  for (let i = 0; i < str.length; i++) {
    const originalChar = str[i];
    const base = normalizeChar(originalChar);

    if (/\w/.test(base)) {
      normalizedChars.push(base);
      indexMap.push(i);
      lastWasSpace = false;
    } else if (/\s/.test(originalChar)) {
      if (!lastWasSpace) {
        normalizedChars.push(" ");
        indexMap.push(i);
        lastWasSpace = true;
      }
    }
    // ignore ponctuation
  }

  return {
    normalized: normalizedChars.join(""),
    indexMap,
  };
}

function renderWithBreaks(text: string) {
  return text.split("\n").map((line, i) => (
    <React.Fragment key={i}>
      {line}
      <br />
    </React.Fragment>
  ));
}

export const HighlightedChunk = ({ chunk, quote }: { chunk: string; quote?: string }) => {
  if (!quote) return renderWithBreaks(chunk);

  const { normalized: normChunk, indexMap } = normalizeWithMapping(chunk);
  const normQuote = normalizeWithMapping(quote).normalized;

  const matchIndex = normChunk.indexOf(normQuote);

  if (matchIndex === -1) {
    return renderWithBreaks(chunk);
  }

  const startOriginal = indexMap[matchIndex];
  const endOriginal = indexMap[matchIndex + normQuote.length - 1] + 1;

  const before = chunk.slice(0, startOriginal);
  const highlight = chunk.slice(startOriginal, endOriginal);
  const after = chunk.slice(endOriginal);

  return (
    <>
      {renderWithBreaks(before)}
      <span style={{ background: "yellow", fontWeight: "bold" }}>
        {renderWithBreaks(highlight)}
      </span>
      {renderWithBreaks(after)}
    </>
  );
};

const ExpandableAlert: FC<ExpandableAlertProps> = ({ title, chunk, maxHeight = 200 }) => {
  const [expanded, setExpanded] = useState(false);
  const hasContent = chunk.chunk && typeof chunk.chunk === "string" && chunk.chunk.length > 0;
  const handleToggle = () => {
    if (hasContent) {
      setExpanded((prev) => !prev);
    }
  };

  return (
    <Alert sx={{ mb: 2, cursor: hasContent ? "pointer" : "default" }} onClick={handleToggle} icon={false}>
      <AlertTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {title}
        {hasContent && (
          <IconButton size="small" disableRipple disableFocusRipple sx={{ pointerEvents: "none" }}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        )}
      </AlertTitle>
      <Collapse in={expanded}>
        <Box
          sx={{
            maxHeight,
            overflowY: "auto",
            pr: 1, // optional: adds space for scrollbar
          }}
        >
          <Typography variant="body2">
            {HighlightedChunk({
              chunk: chunk.chunk,
              quote: chunk.quote,
            })}
          </Typography>
        </Box>
      </Collapse>
    </Alert>
  );
};

export default ExpandableAlert;
